/*
 * Membership cart guard. Lines that may not go to checkout (the gift without
 * shoes, members-only products for non-members) are removed, and checkout
 * waits until the cart has been re-checked. The rules themselves are Liquid
 * (sections/membership-cart-guard.liquid); this re-renders that section after
 * every cart change and acts on its state.
 */
;(() => {
  if (customElements.get('membership-cart-guard')) return

  const SECTION = 'membership-cart-guard'
  const SOURCE = 'membership-cart-guard'
  const MAX_REMOVALS = 3
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'

  class MembershipCartGuard extends HTMLElement {
    connectedCallback() {
      if (this.ready) return
      this.ready = true
      this.removals = 0
      this.requests = 0
      this.known = this.dataset.state

      const bus = window.MinimogEvents
      const events = (window.MinimogTheme && window.MinimogTheme.pubSubEvents) || {}
      if (bus && typeof bus.subscribe === 'function') {
        if (events.cartUpdate) {
          bus.subscribe(events.cartUpdate, (detail) => {
            if (detail && detail.source === SOURCE) return
            this.removals = 0
            this.check()
          })
        }
        if (events.openCartDrawer) bus.subscribe(events.openCartDrawer, () => this.check())
      }

      // Window capture runs before the theme's and apps' own checkout click handlers.
      window.addEventListener('click', (event) => this.guardCheckout(event), true)
      window.addEventListener('submit', (event) => this.guardCheckout(event), true)
      // The rendered state can be older than the cart (another tab, a cached page), so the cart page checks again.
      if (this.dataset.state === 'orphan') this.removeLines()
      else if (window.MinimogSettings && window.MinimogSettings.templateName === 'cart') this.check()
    }

    /*
     * Checkout goes ahead only once a fresh check finds nothing to remove; the
     * click is then replayed so the theme and apps handle it as usual. The check is one small request, so it runs on every checkout.
     */
    guardCheckout(event) {
      const button =
        event.type === 'submit' ? event.submitter : event.target.closest && event.target.closest('[name="checkout"]')
      if (!button || button.name !== 'checkout' || this.passing) return

      event.preventDefault()
      event.stopImmediatePropagation()
      this.removedLines = false
      this.check().then((state) => {
        // Lines just removed leave the shopper on the updated cart.
        if (this.removedLines || (state !== 'ok' && state !== 'none')) return
        this.passing = true
        button.click()
        this.passing = false
      })
    }

    check() {
      const request = ++this.requests
      const addon = window.ProductAddon || {}
      const addsBefore = addon.adds || 0
      const busyBefore = !!addon.pending
      if (this.dataset.state !== 'none') this.dataset.state = 'checking'

      // A superseded request resolves with the newest one, so callers always get the latest state.
      this.pending = fetch(`${root()}?sections=${SECTION}`, {headers: {Accept: 'application/json'}})
        .then((response) => response.json())
        .then((sections) => {
          if (request !== this.requests) return this.pending
          const fresh = new DOMParser()
            .parseFromString(sections[SECTION] || '', 'text/html')
            .querySelector('membership-cart-guard')
          if (!fresh) return this.settle('none')
          this.dataset.keys = fresh.dataset.keys || ''
          // A snapshot taken while an add-on add was under way may show the add-on alone: look again.
          if (fresh.dataset.state === 'orphan' && (busyBefore || (addon.adds || 0) !== addsBefore)) {
            return Promise.resolve(addon.pending).then(() => this.check())
          }
          if (fresh.dataset.state === 'orphan') return this.removeLines()
          return this.settle(fresh.dataset.state)
        })
        .catch(() => this.settle(this.known))
      return this.pending
    }

    settle(state) {
      this.dataset.state = state
      if (state !== 'checking') this.known = state
      return state
    }

    removeLines() {
      // A gift-then-shoe add in progress leaves the gift alone for a moment; judge the cart once it is done.
      const pending = window.ProductAddon && window.ProductAddon.pending
      if (pending) return pending.then(() => this.check())

      const keys = (this.dataset.keys || '').split(',').filter(Boolean)
      if (!keys.length || this.removals >= MAX_REMOVALS) return this.settle('orphan')
      this.removals += 1
      this.dataset.state = 'orphan'

      const updates = Object.fromEntries(keys.map((key) => [key, 0]))
      return fetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({updates}),
      })
        .then((response) => response.json())
        .then((cart) => {
          this.removedLines = true
          this.refreshCart(cart)
          return this.check()
        })
        .catch(() => this.settle('orphan'))
    }

    refreshCart(cart) {
      const drawer = document.querySelector('m-cart-drawer')
      if (drawer && typeof drawer.onCartDrawerUpdate === 'function') drawer.onCartDrawerUpdate()
      const bus = window.MinimogEvents
      const event =
        window.MinimogTheme && window.MinimogTheme.pubSubEvents && window.MinimogTheme.pubSubEvents.cartUpdate
      if (bus && event && typeof bus.emit === 'function') bus.emit(event, {...cart, source: SOURCE})
    }
  }

  customElements.define('membership-cart-guard', MembershipCartGuard)
})()
